import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, ChevronRight, Target, Loader2, Circle, CheckCircle2, Calendar as CalIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/* ============================ Date helpers ============================ */
const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // Monday-first (0=Mon .. 6=Sun)
  d.setDate(d.getDate() - day);
  return d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const safeDate = (dt) => (typeof dt === 'string' && dt) ? dt.split('T')[0] : null;
const safeTime = (dt) => {
  if (typeof dt !== 'string' || !dt) return '';
  const parts = dt.split('T');
  return parts.length > 1 ? parts[1].substring(0, 5) : '';
};

/* ============================ Compact Donut ============================ */
const Donut = ({ pct, color, textColor }) => {
  const R = 26;
  const C = 2 * Math.PI * R;
  const dash = (C * pct) / 100;
  return (
    <div className="relative w-16 h-16">
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r={R} fill="none" stroke={color} strokeOpacity="0.18" strokeWidth="6" />
        <circle cx="32" cy="32" r={R} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} ${C - dash}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 400ms ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-semibold tabular-nums" style={{ color: textColor }}>{pct}%</span>
      </div>
    </div>
  );
};

/* ========================== Day column ========================== */
const DayColumn = ({ date, isToday, isSelected, color, tasks, events, priorities, onToggleTask, onOpenEvent, onSelectDay, onSavePriorities, theme, dateLocale }) => {
  const dstr = ymd(date);
  const dayTasks = (tasks || []).filter(
    (task) => task.scheduled_date === dstr
  );

  const done = dayTasks.filter((task) => !!task.completed).length;
  const total = dayTasks.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  const dayEvents = (events || [])
    .filter((e) => safeDate(e.start_time) === dstr)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  const dayName = date.toLocaleDateString(dateLocale, { weekday: 'long' });

  // Subtle tinted surfaces built from the day color
  const softTint = `${color}12`;
  const softerTint = `${color}08`;
  const borderColor = isToday ? color : theme.border;

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col shrink-0 snap-start min-h-[600px]"
      data-testid={`week-day-${dstr}`}
      onClick={() => onSelectDay(dstr)}
      role="button"
      tabIndex={0}
      style={{
        background: theme.card,
        border: `1px solid ${isSelected ? color : borderColor}`,
        width: '100%',
        boxShadow: isSelected
          ? `0 0 0 1px ${color}55, 0 8px 24px ${color}18`
          : isToday
            ? `0 4px 14px ${color}30`
            : 'none',
        cursor: 'pointer',
      }}
    >
      {/* Header — number + day name aligned */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: softTint }}>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-light leading-none tabular-nums" style={{ color: theme.textPrimary }}>{date.getDate()}</span>
          <span className="text-xs capitalize font-medium" style={{ color: theme.textPrimary }}>{dayName}</span>
        </div>
        <Donut pct={pct} color={color} textColor={theme.textPrimary} />
      </div>

      {/* Compact stats — single row */}
      <div className="flex items-center justify-around px-4 py-2 text-[10px]"
        style={{ borderBottom: `1px solid ${theme.border}` }}>
        <div className="text-center">
          <span className="tabular-nums font-semibold" style={{ color: theme.success }}>{done}</span>
          <span className="ml-1 uppercase tracking-wider" style={{ color: theme.textMuted }}>hecho</span>
        </div>
        <span style={{ color: theme.textMuted }}>·</span>
        <div className="text-center">
          <span className="tabular-nums font-semibold" style={{ color: theme.warning || theme.accent }}>{total - done}</span>
          <span className="ml-1 uppercase tracking-wider" style={{ color: theme.textMuted }}>pend.</span>
        </div>
        <span style={{ color: theme.textMuted }}>·</span>
        <div className="text-center">
          <span className="tabular-nums font-semibold" style={{ color: theme.textPrimary }}>{total}</span>
          <span className="ml-1 uppercase tracking-wider" style={{ color: theme.textMuted }}>total</span>
        </div>
      </div>

      {/* Top 3 prioridades */}
      <div
        className="px-5 py-4"
        style={{ background: softerTint }}
        data-priority-group
      >
        <p className="text-[9px] uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: theme.textMuted }}>
          <Target size={9} /> Top 3 prioridades
        </p>
        <div className="space-y-2">
          {[0, 1, 2].map((index) => (
            <div
              key={`${dstr}-priority-${index}`}
              className="flex items-start gap-2"
            >
              <span
                className="text-[10px] leading-7 tabular-nums shrink-0"
                style={{ color: theme.textMuted }}
              >
                {index + 1}.
              </span>

              <input
                type="text"
                maxLength={160}
                defaultValue={priorities[index] || ''}
                placeholder={`Prioridad ${index + 1}`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                onBlur={(e) => {
                  const inputs = Array.from(
                    e.currentTarget
                      .closest('[data-priority-group]')
                      .querySelectorAll('input')
                  );

                  const items = inputs.map((input) => input.value);

                  onSavePriorities(dstr, items);
                }}
                className="w-full h-7 bg-transparent border-0 outline-none text-[11px] leading-tight px-1 rounded"
                style={{
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tareas — solo título principal, sin desglose */}
      <div className="px-5 py-4 flex-1" style={{ borderTop: `1px solid ${theme.border}` }}>
        <p className="text-[9px] uppercase tracking-widest mb-2" style={{ color: theme.textMuted }}>
          Tareas
        </p>

        {dayTasks.length === 0 ? (
          <p className="text-[10px]" style={{ color: theme.textMuted }}>
            Sin tareas programadas.
          </p>
        ) : (
          <ul className="space-y-2">
            {dayTasks.slice(0, 8).map((task) => {
              const isDone = !!task.completed;

              return (
                <li key={task.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleTask(task);
                    }}
                    aria-label={isDone ? 'Marcar tarea pendiente' : 'Marcar tarea completada'}
                    data-testid={`week-task-${dstr}-${task.id}`}
                    className="shrink-0"
                  >
                    {isDone
                      ? <CheckCircle2 size={13} style={{ color: theme.success }} />
                      : <Circle size={13} style={{ color: theme.textMuted }} />}
                  </button>

                  <span
                    className="text-[11px] leading-tight line-clamp-2"
                    style={{
                      color: isDone ? theme.textMuted : theme.textPrimary,
                      textDecoration: isDone ? 'line-through' : 'none',
                    }}
                  >
                    {task.task || 'Sin título'}
                  </span>
                </li>
              );
            })}

            {dayTasks.length > 8 && (
              <li
                className="text-[10px] italic pt-0.5"
                style={{ color: theme.textMuted }}
              >
                +{dayTasks.length - 8} más
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Agenda — Hora + Evento en línea */}
      <div className="px-5 py-4 min-h-[150px]" style={{ borderTop: `1px solid ${theme.border}`, background: softerTint }}>
        <p className="text-[9px] uppercase tracking-widest mb-1 flex items-center gap-1" style={{ color: theme.textMuted }}>
          <CalIcon size={9} /> Horario
        </p>
        {dayEvents.length === 0 ? (
          <p className="text-[10px]" style={{ color: theme.textMuted }}>Sin eventos.</p>
        ) : (
          <ul className="space-y-0.5">
            {dayEvents.slice(0, 8).map((e) => {
              const hhmm = safeTime(e.start_time);
              return (
                <li key={e.id}>
                  <button onClick={() => onOpenEvent(e)}
                    className="w-full text-left flex items-center gap-2 hover:opacity-80 transition-opacity"
                    data-testid={`week-event-${e.id}`}>
                    <span className="text-[10px] tabular-nums shrink-0" style={{ color: theme.textMuted, minWidth: 30 }}>{hhmm}</span>
                    <span className="text-[11px] leading-tight line-clamp-1 flex-1" style={{ color: theme.textPrimary }}>{e.title || 'Sin título'}</span>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.color || color }} />
                  </button>
                </li>
              );
            })}
            {dayEvents.length > 8 && (
              <li className="text-[10px] italic pt-0.5" style={{ color: theme.textMuted }}>+{dayEvents.length - 8} más</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ============================ Main ============================ */
const WeeklyView = () => {
  const { theme } = useTheme();
  const { dateLocale } = useLanguage();
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [dailyPriorities, setDailyPriorities] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => ymd(new Date()));

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gRes, eRes, pRes, tRes] = await Promise.allSettled([
        api.get('/goals'),
        api.get('/calendar'),
        api.get('/daily-priorities'),
        api.get('/task-breakdown'),
      ]);
      if (gRes.status === 'fulfilled') setGoals(Array.isArray(gRes.value.data) ? gRes.value.data : []);
      if (eRes.status === 'fulfilled') setEvents(Array.isArray(eRes.value.data) ? eRes.value.data : []);
      if (tRes.status === 'fulfilled') {
        setTasks(Array.isArray(tRes.value.data) ? tRes.value.data : []);
      }

      if (pRes.status === 'fulfilled') {
        const map = {};
        const list = Array.isArray(pRes.value.data) ? pRes.value.data : [];

        list.forEach((entry) => {
          if (!entry?.date) return;
          map[entry.date] = Array.isArray(entry.items) ? entry.items : [];
        });

        setDailyPriorities(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openEvent = (ev) => {
    navigate(`/calendar?event=${ev.id}`);
  };

  const toggleTask = async (task) => {
    const previousCompleted = !!task.completed;
    const nextCompleted = !previousCompleted;

    setTasks((prev) =>
      prev.map((item) =>
        item.id === task.id
          ? { ...item, completed: nextCompleted }
          : item
      )
    );

    try {
      await api.put(`/task-breakdown/${task.id}`, {
        completed: nextCompleted,
      });
    } catch {
      setTasks((prev) =>
        prev.map((item) =>
          item.id === task.id
            ? { ...item, completed: previousCompleted }
            : item
        )
      );

      toast.error('No pude actualizar la tarea. Vuelve a probar.');
    }
  };

  const saveDailyPriorities = async (dateStr, items) => {
    const cleaned = (items || [])
      .map((item) => typeof item === 'string' ? item.trim() : '')
      .filter(Boolean)
      .slice(0, 3);

    const previous = dailyPriorities[dateStr] || [];

    setDailyPriorities((prev) => ({
      ...prev,
      [dateStr]: cleaned,
    }));

    try {
      const response = await api.put(`/daily-priorities/${dateStr}`, {
        items: cleaned,
      });

      setDailyPriorities((prev) => ({
        ...prev,
        [dateStr]: Array.isArray(response.data?.items)
          ? response.data.items
          : cleaned,
      }));
    } catch {
      setDailyPriorities((prev) => ({
        ...prev,
        [dateStr]: previous,
      }));

      toast.error('No pude guardar las prioridades. Vuelve a probar.');
    }
  };

  const priorityGoals = useMemo(() => (goals || [])
    .filter((g) => !g.completed)
    .sort((a, b) => {
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    }), [goals]);

  const summary = useMemo(() => {
    const weekDates = new Set(days.map((d) => ymd(d)));

    const weekTasks = (tasks || []).filter(
      (task) => task.scheduled_date && weekDates.has(task.scheduled_date)
    );

    const total = weekTasks.length;
    const done = weekTasks.filter((task) => !!task.completed).length;
    const avg = total === 0 ? 0 : Math.round((done / total) * 100);
    const priorityDone = (goals || []).filter((g) => g.completed).length;

    return {
      avg,
      done,
      pending: total - done,
      priorityDone,
      total,
    };
  }, [days, tasks, goals]);

  const rangeLabel = `${days[0].toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const isCurrentWeek = isSameDay(anchor, startOfWeek(new Date()));
  const today = new Date();

  const selectedDay = useMemo(
    () => days.find((d) => ymd(d) === selectedDate) || days[0],
    [days, selectedDate]
  );

  const selectedDayTasks = useMemo(() => {
    if (!selectedDay) return [];

    const dstr = ymd(selectedDay);

    return (tasks || []).filter(
      (task) => task.scheduled_date === dstr
    );
  }, [tasks, selectedDay]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDay) return [];

    const dstr = ymd(selectedDay);

    return (events || [])
      .filter((e) => safeDate(e.start_time) === dstr)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  }, [events, selectedDay]);

  const selectedDayCompletedTasks = useMemo(
    () => selectedDayTasks.filter((task) => !!task.completed).length,
    [selectedDayTasks]
  );

  const miniCalendarLabel = anchor.toLocaleDateString(dateLocale, {
    month: 'long',
    year: 'numeric',
  });

  const miniCalendarDays = useMemo(() => {
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(firstOfMonth);

    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [anchor]);

  return (
    <div className="space-y-5" data-testid="weekly-view">
      {/* Compact header with inline summary */}
      <div className="rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap"
        style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <button onClick={() => setAnchor(addDays(anchor, -7))}
          className="p-1.5 rounded-md hover:opacity-80" style={{ color: theme.textSecondary }}
          data-testid="week-prev">
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 min-w-[180px]">
          <p className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Semana</p>
          <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{rangeLabel}</p>
        </div>
        <button onClick={() => setAnchor(addDays(anchor, 7))}
          className="p-1.5 rounded-md hover:opacity-80" style={{ color: theme.textSecondary }}
          data-testid="week-next">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setAnchor(startOfWeek(new Date()))} disabled={isCurrentWeek}
          className="text-xs px-3 py-1.5 rounded-md disabled:opacity-40 font-medium"
          style={{ background: theme.accent, color: theme.bg }}
          data-testid="week-today">
          Hoy
        </button>

        {/* Inline summary — one row */}
        <div className="flex items-center gap-4 sm:gap-6 pl-3 ml-1 border-l" style={{ borderColor: theme.border }}>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold tabular-nums" style={{ color: theme.accent }}>{summary.avg}%</span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Prod.</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold tabular-nums" style={{ color: theme.success }}>{summary.done}</span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Hecho</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold tabular-nums" style={{ color: theme.warning || theme.accent }}>{summary.pending}</span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Pend.</span>
          </div>
          <div className="hidden sm:flex items-baseline gap-1.5">
            <span className="text-lg font-semibold tabular-nums" style={{ color: theme.textPrimary }}>{summary.priorityDone}</span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Metas</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: theme.textMuted }}>
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Cargando semana…
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* Weekly sidebar shell — content will be expanded in the next step */}
          <aside
            className="w-full lg:w-[240px] lg:shrink-0 rounded-2xl p-5 space-y-5"
            style={{
              background: theme.card,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div>
              <p
                className="text-[10px] uppercase tracking-[0.18em]"
                style={{ color: theme.textMuted }}
              >
                Calendario
              </p>
              <p
                className="text-sm font-medium capitalize mt-1"
                style={{ color: theme.textPrimary }}
              >
                {miniCalendarLabel}
              </p>
            </div>

            <div>
              <div
                className="grid grid-cols-7 gap-1 mb-2 text-[9px] text-center uppercase"
                style={{ color: theme.textMuted }}
              >
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {miniCalendarDays.map((d) => {
                  const dstr = ymd(d);
                  const inMonth = d.getMonth() === anchor.getMonth();
                  const inCurrentWeek = days.some((weekDay) => isSameDay(weekDay, d));
                  const isSelected = selectedDate === dstr;
                  const isTodayDate = isSameDay(d, today);

                  return (
                    <button
                      key={dstr}
                      onClick={() => {
                        setAnchor(startOfWeek(d));
                        setSelectedDate(dstr);
                      }}
                      className="h-7 rounded-md text-[10px] tabular-nums transition-all"
                      style={{
                        background: isSelected
                          ? theme.primary
                          : inCurrentWeek
                            ? `${theme.primary}18`
                            : 'transparent',
                        color: isSelected
                          ? theme.bg
                          : inMonth
                            ? theme.textPrimary
                            : theme.textMuted,
                        border: isTodayDate
                          ? `1px solid ${theme.primary}`
                          : '1px solid transparent',
                        opacity: inMonth ? 1 : 0.45,
                      }}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="pt-4"
              style={{ borderTop: `1px solid ${theme.border}` }}
            >
              <p
                className="text-[10px] uppercase tracking-[0.18em] mb-3"
                style={{ color: theme.textMuted }}
              >
                Progreso semanal
              </p>

              <div className="flex items-center gap-4">
                <Donut
                  pct={summary.avg}
                  color={theme.primary}
                  textColor={theme.textPrimary}
                />

                <div>
                  <p
                    className="text-2xl font-semibold tabular-nums"
                    style={{ color: theme.accent }}
                  >
                    {summary.avg}%
                  </p>
                  <p className="text-xs" style={{ color: theme.textSecondary }}>
                    {summary.done} de {summary.total}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="pt-4 space-y-2"
              style={{ borderTop: `1px solid ${theme.border}` }}
            >
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: theme.textMuted }}>Completados</span>
                <span
                  className="font-medium tabular-nums"
                  style={{ color: theme.success }}
                >
                  {summary.done}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span style={{ color: theme.textMuted }}>Pendientes</span>
                <span
                  className="font-medium tabular-nums"
                  style={{ color: theme.warning || theme.accent }}
                >
                  {summary.pending}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span style={{ color: theme.textMuted }}>Metas completadas</span>
                <span
                  className="font-medium tabular-nums"
                  style={{ color: theme.accent }}
                >
                  {summary.priorityDone}
                </span>
              </div>
            </div>
          </aside>

          {/* Day rail — fixed comfortable card width instead of squeezing 7 columns */}
          <div className="w-full min-w-0 overflow-x-auto pb-2 snap-x snap-mandatory">
            <div
              className="flex gap-4 min-w-max"
              style={{ scrollBehavior: 'smooth' }}
            >
              {days.map((d) => (
                <div
                  key={ymd(d)}
                  className="w-[300px] xl:w-[320px] shrink-0"
                >
                  <DayColumn
                    date={d}
                    isToday={isSameDay(d, today)}
                    isSelected={selectedDate === ymd(d)}
                    color={theme.primary}
                    tasks={tasks}
                    events={events}
                    priorities={dailyPriorities[ymd(d)] || []}
                    onToggleTask={toggleTask}
                    onOpenEvent={openEvent}
                    onSelectDay={setSelectedDate}
                    onSavePriorities={saveDailyPriorities}
                    theme={theme}
                    dateLocale={dateLocale}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && selectedDay && (
        <section
          className="rounded-2xl p-5"
          style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
          }}
        >
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <div>
              <p
                className="text-[10px] uppercase tracking-[0.18em]"
                style={{ color: theme.textMuted }}
              >
                Detalle del día
              </p>
              <h3
                className="text-lg font-medium capitalize"
                style={{ color: theme.textPrimary }}
              >
                {selectedDay.toLocaleDateString(dateLocale, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </h3>
            </div>

            <span
              className="text-xs px-3 py-1.5 rounded-full"
              style={{
                background: `${theme.primary}18`,
                color: theme.accent,
                border: `1px solid ${theme.border}`,
              }}
            >
              {selectedDayCompletedTasks} completadas
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div
              className="rounded-xl p-4"
              style={{
                background: theme.elevated,
                border: `1px solid ${theme.border}`,
              }}
            >
              <p
                className="text-[10px] uppercase tracking-widest mb-3"
                style={{ color: theme.textMuted }}
              >
                Tareas del día
              </p>

              {selectedDayTasks.length === 0 ? (
                <p className="text-xs" style={{ color: theme.textMuted }}>
                  Sin tareas programadas.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedDayTasks.map((task) => {
                    const isDone = !!task.completed;

                    return (
                      <li key={task.id} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleTask(task)}
                          aria-label={isDone ? 'Marcar tarea pendiente' : 'Marcar tarea completada'}
                          className="shrink-0"
                        >
                          {isDone
                            ? <CheckCircle2 size={14} style={{ color: theme.success }} />
                            : <Circle size={14} style={{ color: theme.textMuted }} />}
                        </button>

                        <span
                          className="text-xs"
                          style={{
                            color: isDone ? theme.textMuted : theme.textPrimary,
                            textDecoration: isDone ? 'line-through' : 'none',
                          }}
                        >
                          {task.task || 'Sin título'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div
              className="rounded-xl p-4"
              style={{
                background: theme.elevated,
                border: `1px solid ${theme.border}`,
              }}
            >
              <p
                className="text-[10px] uppercase tracking-widest mb-3"
                style={{ color: theme.textMuted }}
              >
                Horario
              </p>

              {selectedDayEvents.length === 0 ? (
                <p className="text-xs" style={{ color: theme.textMuted }}>
                  Sin eventos programados.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedDayEvents.map((e) => (
                    <li key={e.id}>
                      <button
                        onClick={() => openEvent(e)}
                        className="w-full text-left flex items-center gap-3 hover:opacity-80 transition-opacity"
                      >
                        <span
                          className="text-xs tabular-nums shrink-0"
                          style={{
                            color: theme.textMuted,
                            minWidth: 36,
                          }}
                        >
                          {safeTime(e.start_time)}
                        </span>

                        <span
                          className="text-xs flex-1"
                          style={{ color: theme.textPrimary }}
                        >
                          {e.title || 'Sin título'}
                        </span>

                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: e.color || theme.primary }}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              className="rounded-xl p-4"
              style={{
                background: theme.elevated,
                border: `1px solid ${theme.border}`,
              }}
            >
              <p
                className="text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5"
                style={{ color: theme.textMuted }}
              >
                <Target size={11} />
                Prioridades
              </p>

              {priorityGoals.length === 0 ? (
                <p className="text-xs" style={{ color: theme.textMuted }}>
                  Sin metas activas.
                </p>
              ) : (
                <ol
                  className="space-y-2 list-decimal list-inside"
                  style={{ color: theme.textSecondary }}
                >
                  {priorityGoals.slice(0, 3).map((g) => (
                    <li key={g.id} className="text-xs">
                      {g.title}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default WeeklyView;
